/**
 * NavDropdown, Sprint 2.5a A.0/B/S2 (Heidi-pattern mega-dropdown).
 *
 * Two-pane mega-dropdown that anchors under a nav trigger. Pattern
 * lifted from heidihealth.com:
 *   • Click the trigger → backdrop blurs the rest of the page
 *   • A wide white panel anchors under the trigger
 *   • LEFT pane: 3-5 categories with icons + chevrons
 *   • RIGHT pane: contextual sub-items (8-12) with icons + labels;
 *     swaps based on which left category is hovered
 *   • Optional `topBanner` slot above the panes (used by Catering to
 *     flag "this is a separate platform", Lucky's call)
 *
 * Why click-to-open (not hover-to-open): hover-only triggers open
 * unintentionally when the cursor crosses the nav, which breaks
 * scroll-by-cursor patterns and surprises users. Heidi's behaviour
 * looks click-triggered too (the dropdown only opened reliably on
 * click during my study). Click is also more accessible, Stanley.
 *
 * Click-outside + Esc both close. Tab cycles through visible links.
 */

import {
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentType,
  type SVGProps,
} from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";

/** A leaf item rendered in the right pane. */
export interface NavDropdownLeafItem {
  label: string;
  href: string;
  /** Lucide icon component (or any (props: SVGProps) => JSX.Element). */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Set to true if `href` should escape the SPA (use a real <a>). */
  external?: boolean;
}

/** A category in the left pane that gates a list of leaf items in
 *  the right pane. */
export interface NavDropdownCategory {
  id: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** The leaf items rendered in the right pane when this category
   *  is the active one. */
  items: NavDropdownLeafItem[];
}

interface NavDropdownProps {
  /** Trigger label (e.g. "Built for", "Catering"). */
  label: string;
  /** Categories in the left pane. The first one is active by default. */
  categories: NavDropdownCategory[];
  /** Optional banner rendered ABOVE the two-pane area (used by
   *  Catering to flag "separate platform"). */
  topBanner?: ReactNode;
}

export function NavDropdown({ label, categories, topBanner }: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const [activeCatId, setActiveCatId] = useState(categories[0]?.id ?? "");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const activeCat = categories.find((c) => c.id === activeCatId) ?? categories[0];

  // Close on outside click. We bind on the document because the panel
  // overlays a backdrop, we want clicks anywhere outside the panel
  // (including on the backdrop) to close. The trigger has its own
  // toggle handler, so we explicitly skip clicks on it.
  //
  // We listen on `click` rather than `mousedown` because:
  //   1. `click` fires for real mouse, touch, AND programmatic
  //      `.click()` calls (mousedown does not on the latter two
  //      reliably across all browsers).
  //   2. React's onClick on the trigger runs FIRST in the bubble
  //      phase (synchronously sets state); the document listener
  //      then fires and either skips (if target is the own trigger)
  //      or closes the OTHER open dropdown.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]);

  // Esc closes + restores focus to trigger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Reset active category every time the panel opens, fresh UX,
  // never lands you in a stale state from a previous session.
  useEffect(() => {
    if (open) setActiveCatId(categories[0]?.id ?? "");
  }, [open, categories]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        className={`inline-flex items-center gap-1 rounded-full px-4 py-2 lg:px-5 lg:py-2 text-sm lg:text-[15px] font-medium transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/40 ${
          open
            ? "text-coral bg-white/80"
            : "text-navy hover:bg-white/60"
        }`}
        style={{ fontFamily: "DM Sans, sans-serif" }}
      >
        {label}
        <ChevronDown
          size={14}
          strokeWidth={2.5}
          style={{
            transition: "transform 220ms",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {open && (
        <>
          {/* Backdrop, dims + blurs the rest of the page. The pill
              nav itself stays visible because it has higher z-index. */}
          <div
            aria-hidden="true"
            className="fixed inset-0 z-40 transition-opacity"
            style={{
              top: 80, // start below the fixed nav
              backgroundColor: "rgba(20,20,30,0.18)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              animation: "navdropFadeIn 180ms ease-out",
            }}
          />

          {/* Panel, anchored under the trigger via fixed positioning.
              The Index.tsx wrapper centres the pill nav at top:0; we
              just need to drop the panel down by the nav height. */}
          <div
            ref={panelRef}
            id={panelId}
            role="menu"
            className="fixed left-1/2 -translate-x-1/2 z-50 rounded-3xl bg-white shadow-2xl"
            style={{
              top: 78,
              width: "min(640px, calc(100vw - 32px))",
              animation: "navdropPanelIn 220ms cubic-bezier(0.16, 1, 0.3, 1)",
              border: "1px solid rgba(29,52,97,0.06)",
            }}
          >
            {topBanner && (
              <div
                className="px-5 py-3 border-b"
                style={{
                  borderColor: "rgba(29,52,97,0.06)",
                  background: "linear-gradient(180deg, rgba(247,232,164,0.18) 0%, rgba(247,232,164,0) 100%)",
                  borderTopLeftRadius: 24,
                  borderTopRightRadius: 24,
                }}
              >
                {topBanner}
              </div>
            )}

            <div className="grid" style={{ gridTemplateColumns: "minmax(180px, 220px) 1fr" }}>
              {/* LEFT, categories */}
              <div className="py-3 px-2 border-r" style={{ borderColor: "rgba(29,52,97,0.06)" }}>
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = cat.id === activeCatId;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onMouseEnter={() => setActiveCatId(cat.id)}
                      onFocus={() => setActiveCatId(cat.id)}
                      className={`group flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/40 ${
                        isActive
                          ? "bg-cream/80 text-navy"
                          : "text-navy/85 hover:bg-cream/60"
                      }`}
                      style={{ fontFamily: "DM Sans, sans-serif" }}
                    >
                      <span className="inline-flex items-center gap-2.5">
                        <Icon
                          width={16}
                          height={16}
                          strokeWidth={1.8}
                          className={isActive ? "text-coral" : "text-navy/55"}
                        />
                        {cat.label}
                      </span>
                      <ChevronRight
                        size={13}
                        strokeWidth={2}
                        className={isActive ? "text-coral" : "text-navy/35"}
                      />
                    </button>
                  );
                })}
              </div>

              {/* RIGHT, leaves of active category */}
              <div className="py-3 px-2">
                {activeCat?.items.map((leaf) => {
                  const Icon = leaf.icon;
                  const className =
                    "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-navy/90 transition-colors hover:bg-cream/60 focus-visible:bg-cream/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/30";
                  const inner = (
                    <>
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-cream/70 group-hover:bg-coral/10 transition-colors">
                        <Icon
                          width={14}
                          height={14}
                          strokeWidth={1.8}
                          className="text-navy/70 group-hover:text-coral"
                        />
                      </span>
                      <span style={{ fontFamily: "DM Sans, sans-serif" }}>{leaf.label}</span>
                    </>
                  );
                  return leaf.external ? (
                    <a
                      key={leaf.label}
                      href={leaf.href}
                      onClick={() => setOpen(false)}
                      className={className}
                    >
                      {inner}
                    </a>
                  ) : (
                    <Link
                      key={leaf.label}
                      to={leaf.href}
                      onClick={() => setOpen(false)}
                      className={className}
                    >
                      {inner}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          <style>{`
            @keyframes navdropFadeIn {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
            @keyframes navdropPanelIn {
              from { opacity: 0; transform: translate(-50%, -8px); }
              to   { opacity: 1; transform: translate(-50%, 0); }
            }
          `}</style>
        </>
      )}
    </>
  );
}
